/**
 * Phase 4 — Control Center "Tool Access" tab.
 *
 * Per-tool, per-employee MCP enablement for the active brand. Sits inside the
 * Control Center page tab strip (`?tab=tool-access`) and shares the same brand
 * bar as every other CC tab.
 *
 * Layout:
 *   - Top bar: employee picker, "Apply preset" dropdown (Read-only / No writes
 *     / Full + any custom), and a Re-scan button (refetches the tool set).
 *   - Grid: rows = tools grouped by connection/server (collapsible). Each row
 *     shows `exposed_name`, its `ai_description` (inline + on hover), and a `⚠`
 *     badge when `classification === 'write'`. The cell is a toggle for the
 *     selected employee's `employee_enabled`; rows where `is_overridden === false`
 *     render lighter with a small "default" tag (inherited from the connection
 *     policy / preset rather than an explicit per-employee override).
 *   - Inline alias edit (✎) per row → edits `exposed_name` via the PATCH
 *     endpoint; client-side validates `^[a-z0-9_-]{1,128}$` and surfaces the
 *     server 422.
 *   - Bulk: multi-select tools + one-or-more employees → enable/disable
 *     together via the bulk endpoint.
 *
 * RBAC: owner/team_lead (`mcp.access.manage`) can toggle/edit; manager is
 * view-only; analyst/read_only never reach this tab (no nav entry + gated tab).
 *
 * Built in the legacy SCSS style — no MUI. Styles live in
 * `styles/pages/control-center/toolAccess.scss`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainSquareButton from '../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import {
  reloadArrowsSVG,
  editSVG,
  warningSVG,
  chipSVG,
  tickSVG,
  crossSmallSVG,
  filterSVG,
} from '../../lib/allCustomSVGs';
import {
  ToolAccessRow,
  ToolAccessEmployee,
  ToolPreset,
  getToolAccess,
  setToolAccess,
  bulkSetToolAccess,
  applyToolPreset,
  listToolPresets,
  updateToolMeta,
} from '../../lib/api/tool-access/tool-access-api';

// Client mirror of the server's `exposed_name` validation. Keep in lock-step
// with the backend rule so the inline alias edit fails fast before the PATCH.
const EXPOSED_NAME_RE = /^[a-z0-9_-]{1,128}$/;

interface ToolAccessTabProps {
  /** The active brand the grid scopes to (CC's shared brand bar). */
  activeShopSlug?: string;
  /** The agency Bearer token value ('' when absent — the API would 401).
   *  Passed by VALUE so a mid-page session swap (admin "login as seller"
   *  handoff) re-keys the fetch effects. */
  agencyToken: string;
  /** owner/team_lead — may toggle/edit. Manager is view-only (false). */
  canManage: boolean;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
  showInfo: (msg: string) => void;
}

/** A connection group (collapsible section) derived from the flat tool rows. */
interface ConnectionGroup {
  connectionId: number | string;
  connectionName: string;
  platformKey: string | null;
  rows: ToolAccessRow[];
}

/** Group the flat tool rows by their owning connection, preserving order. */
function groupByConnection(rows: ToolAccessRow[]): ConnectionGroup[] {
  const order: (number | string)[] = [];
  const map = new Map<number | string, ConnectionGroup>();
  rows.forEach((row) => {
    if (!map.has(row.connection_id)) {
      order.push(row.connection_id);
      map.set(row.connection_id, {
        connectionId: row.connection_id,
        connectionName: row.connection_name,
        platformKey: row.platform_key,
        rows: [],
      });
    }
    map.get(row.connection_id)!.rows.push(row);
  });
  return order.map((id) => map.get(id)!);
}

const ToolAccessTab = ({
  activeShopSlug,
  agencyToken,
  canManage,
  showError,
  showSuccess,
  showInfo,
}: ToolAccessTabProps) => {
  const { t } = useTranslation();
  const hasAgencyToken = Boolean(agencyToken);

  // ----------------------- data -----------------------
  const [rows, setRows] = useState<ToolAccessRow[]>([]);
  const [employees, setEmployees] = useState<ToolAccessEmployee[]>([]);
  const [presets, setPresets] = useState<ToolPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');

  // ----------------------- per-row in-flight flags -----------------------
  // toggle mutations in flight, keyed by `mcp_tool_policy_id`
  const [toggleBusy, setToggleBusy] = useState<Set<string>>(() => new Set());
  const [presetApplying, setPresetApplying] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  // ----------------------- collapse state -----------------------
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  // ----------------------- bulk selection -----------------------
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedToolIds, setSelectedToolIds] = useState<Set<string>>(
    () => new Set()
  );
  const [bulkEmployeeIds, setBulkEmployeeIds] = useState<Set<string>>(
    () => new Set()
  );
  const [bulkBusy, setBulkBusy] = useState(false);

  // ----------------------- alias edit -----------------------
  const [aliasEdit, setAliasEdit] = useState<{
    policyId: number | string;
    connectionId: number | string;
    toolName: string;
    value: string;
  } | null>(null);
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [aliasSaving, setAliasSaving] = useState(false);

  // ----------------------- load -----------------------
  const loadPresets = useCallback(async () => {
    try {
      const list = await listToolPresets();
      setPresets(list);
    } catch (err: any) {
      // Presets are non-fatal — the grid still works without them. Log only.
      // eslint-disable-next-line no-console
      console.warn('[ToolAccessTab] preset load failed', err);
      setPresets([]);
    }
  }, []);

  const load = useCallback(
    async (shopSlug: string, employeeId?: string) => {
      setLoading(true);
      try {
        const res = await getToolAccess(shopSlug, employeeId);
        setRows(res.tools);
        setEmployees(res.employees);
        // Resolve the effective selected employee: keep the current selection
        // if it's still valid, otherwise default to the first returned one.
        setSelectedEmployeeId((prev) => {
          if (prev && res.employees.some((e) => e.id === prev)) return prev;
          return res.employees[0]?.id ?? '';
        });
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[ToolAccessTab] load failed', err);
        if (err?.status === 403) {
          showError(t('ToolAccessNoPermission'));
        } else {
          showError(err?.message || t('ToolAccessLoadError'));
        }
        setRows([]);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    },
    // showError/t are stable enough for this loader; intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initial + on brand change: fetch presets once and the grid for the brand.
  useEffect(() => {
    if (activeShopSlug && hasAgencyToken) {
      loadPresets();
      load(activeShopSlug);
    } else {
      setRows([]);
      setEmployees([]);
      setPresets([]);
      setLoading(false);
    }
    // reset transient UI on brand change
    setBulkMode(false);
    setSelectedToolIds(new Set());
    setBulkEmployeeIds(new Set());
    setAliasEdit(null);
    setAliasError(null);
  }, [activeShopSlug, agencyToken, load, loadPresets]);

  // When the selected employee changes, refetch their effective enablement.
  // (Skip the very first render where `load` already ran without an employee.)
  const [didInitialLoad, setDidInitialLoad] = useState(false);
  useEffect(() => {
    if (!didInitialLoad) {
      if (selectedEmployeeId) setDidInitialLoad(true);
      return;
    }
    if (activeShopSlug && hasAgencyToken && selectedEmployeeId) {
      load(activeShopSlug, selectedEmployeeId);
    }
    // load is stable; only react to the selected employee / brand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId]);

  // ----------------------- derived -----------------------
  const groups = useMemo(() => groupByConnection(rows), [rows]);
  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedEmployeeId),
    [employees, selectedEmployeeId]
  );
  const writeCount = useMemo(
    () => rows.filter((r) => r.classification === 'write').length,
    [rows]
  );

  // ----------------------- toggle one cell -----------------------
  const handleToggle = async (row: ToolAccessRow) => {
    if (!activeShopSlug || !canManage || !selectedEmployeeId) return;
    const key = String(row.mcp_tool_policy_id);
    if (toggleBusy.has(key)) return;
    const nextEnabled = !row.employee_enabled;

    // optimistic — flip the cell and mark it as an explicit override
    setRows((prev) =>
      prev.map((r) =>
        r.mcp_tool_policy_id === row.mcp_tool_policy_id
          ? { ...r, employee_enabled: nextEnabled, is_overridden: true }
          : r
      )
    );
    setToggleBusy((prev) => new Set(prev).add(key));

    try {
      await setToolAccess(activeShopSlug, {
        mcp_tool_policy_id: row.mcp_tool_policy_id,
        agency_employee_id: selectedEmployeeId,
        enabled: nextEnabled,
      });
      showSuccess(
        t('ToolAccessToggleUpdated', { tool: row.exposed_name })
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ToolAccessTab] toggle failed', err);
      // roll back to the previous state
      setRows((prev) =>
        prev.map((r) =>
          r.mcp_tool_policy_id === row.mcp_tool_policy_id
            ? {
                ...r,
                employee_enabled: row.employee_enabled,
                is_overridden: row.is_overridden,
              }
            : r
        )
      );
      showError(err?.message || t('ToolAccessToggleFailed'));
    } finally {
      setToggleBusy((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // ----------------------- apply preset -----------------------
  const handleApplyPreset = async (presetKey: string) => {
    if (!activeShopSlug || !canManage || !selectedEmployeeId || !presetKey) {
      return;
    }
    setPresetApplying(true);
    try {
      await applyToolPreset(activeShopSlug, presetKey, selectedEmployeeId);
      const preset = presets.find((p) => p.key === presetKey);
      showSuccess(
        t('ToolAccessPresetApplied', {
          preset: preset?.name || presetKey,
          employee: selectedEmployee?.name || '',
        })
      );
      // the preset rewrote this employee's overrides — refetch their grid
      await load(activeShopSlug, selectedEmployeeId);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ToolAccessTab] apply preset failed', err);
      showError(err?.message || t('ToolAccessPresetFailed'));
    } finally {
      setPresetApplying(false);
    }
  };

  // ----------------------- re-scan -----------------------
  const handleRescan = async () => {
    if (!activeShopSlug || !hasAgencyToken) return;
    setRescanning(true);
    try {
      await load(activeShopSlug, selectedEmployeeId || undefined);
      showInfo(t('ToolAccessRescanned'));
    } finally {
      setRescanning(false);
    }
  };

  // ----------------------- collapse toggle -----------------------
  const toggleCollapse = (connectionId: number | string) => {
    const key = String(connectionId);
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ----------------------- bulk helpers -----------------------
  const toggleBulkMode = () => {
    setBulkMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedToolIds(new Set());
        setBulkEmployeeIds(new Set());
      } else if (selectedEmployeeId) {
        // seed the bulk-employee selection with the currently-viewed employee
        setBulkEmployeeIds(new Set([selectedEmployeeId]));
      }
      return next;
    });
  };

  const toggleToolSelected = (policyId: number | string) => {
    const key = String(policyId);
    setSelectedToolIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleBulkEmployee = (employeeId: string) => {
    setBulkEmployeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const selectAllToolsInGroup = (group: ConnectionGroup, select: boolean) => {
    setSelectedToolIds((prev) => {
      const next = new Set(prev);
      group.rows.forEach((r) => {
        const key = String(r.mcp_tool_policy_id);
        if (select) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const runBulk = async (enabled: boolean) => {
    if (!activeShopSlug || !canManage) return;
    if (selectedToolIds.size === 0 || bulkEmployeeIds.size === 0) {
      showError(t('ToolAccessBulkNothingSelected'));
      return;
    }
    // Build the cartesian product of selected tools × selected employees.
    const overrides = Array.from(selectedToolIds).flatMap((policyId) =>
      Array.from(bulkEmployeeIds).map((employeeId) => ({
        mcp_tool_policy_id: policyId,
        agency_employee_id: employeeId,
        enabled,
      }))
    );
    setBulkBusy(true);
    try {
      await bulkSetToolAccess(activeShopSlug, overrides);
      showSuccess(
        t('ToolAccessBulkApplied', {
          count: overrides.length,
          state: enabled
            ? t('ToolAccessEnabled')
            : t('ToolAccessDisabled'),
        })
      );
      setBulkMode(false);
      setSelectedToolIds(new Set());
      setBulkEmployeeIds(new Set());
      // refetch the currently-viewed employee so the grid reflects the change
      await load(activeShopSlug, selectedEmployeeId || undefined);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ToolAccessTab] bulk failed', err);
      showError(err?.message || t('ToolAccessBulkFailed'));
    } finally {
      setBulkBusy(false);
    }
  };

  // ----------------------- alias edit -----------------------
  const openAliasEdit = (row: ToolAccessRow) => {
    setAliasError(null);
    setAliasEdit({
      policyId: row.mcp_tool_policy_id,
      connectionId: row.connection_id,
      toolName: row.tool_name,
      value: row.exposed_name,
    });
  };

  const closeAliasEdit = () => {
    if (aliasSaving) return;
    setAliasEdit(null);
    setAliasError(null);
  };

  const saveAliasEdit = async () => {
    if (!activeShopSlug || !aliasEdit || !canManage) return;
    const next = aliasEdit.value.trim();
    if (!EXPOSED_NAME_RE.test(next)) {
      setAliasError(t('ToolAccessAliasInvalid'));
      return;
    }
    setAliasSaving(true);
    setAliasError(null);
    try {
      const updated = await updateToolMeta(
        activeShopSlug,
        aliasEdit.connectionId,
        aliasEdit.toolName,
        { exposed_name: next }
      );
      const newExposed = updated.exposed_name ?? next;
      // patch the row in place (no full refetch needed for a rename)
      setRows((prev) =>
        prev.map((r) =>
          r.mcp_tool_policy_id === aliasEdit.policyId
            ? { ...r, exposed_name: newExposed }
            : r
        )
      );
      showSuccess(t('ToolAccessAliasUpdated', { tool: newExposed }));
      setAliasEdit(null);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ToolAccessTab] alias update failed', err);
      if (err?.status === 422) {
        if (err?.validation) {
          const details = Object.entries(
            err.validation as Record<string, string[]>
          )
            .map(([k, msgs]) => `${k}: ${msgs.join(', ')}`)
            .join(' · ');
          setAliasError(`${t('ToolAccessAliasInvalid')} — ${details}`);
        } else {
          setAliasError(err?.message || t('ToolAccessAliasInvalid'));
        }
      } else {
        setAliasError(err?.message || t('ToolAccessAliasFailed'));
      }
    } finally {
      setAliasSaving(false);
    }
  };

  // ----------------------- render: top bar -----------------------
  const renderTopBar = () => (
    <div className='toolAccess__bar'>
      {/* Employee picker — drives which employee's enablement the grid shows. */}
      <div className='toolAccess__bar-group'>
        <label
          className='toolAccess__bar-label'
          htmlFor='ta-employee-select'
        >
          {t('ToolAccessEmployeeLabel')}
        </label>
        <div className='toolAccess__selectWrap'>
          <select
            id='ta-employee-select'
            className='toolAccess__select'
            value={selectedEmployeeId}
            disabled={loading || employees.length === 0}
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
          >
            {employees.length === 0 && (
              <option value=''>{t('ToolAccessNoEmployees')}</option>
            )}
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name} ({emp.role})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Apply preset — owner/team_lead only. */}
      {canManage && (
        <div className='toolAccess__bar-group'>
          <label className='toolAccess__bar-label' htmlFor='ta-preset-select'>
            {t('ToolAccessPresetLabel')}
          </label>
          <div className='toolAccess__selectWrap'>
            <select
              id='ta-preset-select'
              className='toolAccess__select'
              value=''
              disabled={
                presetApplying ||
                !selectedEmployeeId ||
                presets.length === 0
              }
              onChange={(e) => {
                const key = e.target.value;
                if (key) handleApplyPreset(key);
                // reset to placeholder so re-selecting the same preset re-fires
                e.target.value = '';
              }}
            >
              <option value='' disabled>
                {presets.length === 0
                  ? t('ToolAccessNoPresets')
                  : t('ToolAccessPresetPlaceholder')}
              </option>
              {presets.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className='toolAccess__bar-actions'>
        {canManage && rows.length > 0 && (
          <button
            type='button'
            className={`toolAccess__iconBtn ${
              bulkMode ? 'toolAccess__iconBtn--active' : ''
            }`}
            onClick={toggleBulkMode}
          >
            <CustomSVGs svg={filterSVG} />
            {bulkMode ? t('ToolAccessBulkExit') : t('ToolAccessBulkMode')}
          </button>
        )}
        <button
          type='button'
          className='toolAccess__iconBtn'
          disabled={rescanning || loading}
          onClick={handleRescan}
        >
          <CustomSVGs svg={reloadArrowsSVG} />
          {rescanning ? t('ToolAccessRescanning') : t('ToolAccessRescan')}
        </button>
      </div>
    </div>
  );

  // ----------------------- render: bulk bar -----------------------
  const renderBulkBar = () => {
    if (!bulkMode) return null;
    return (
      <div className='toolAccess__bulkBar'>
        <div className='toolAccess__bulkBar-meta'>
          {t('ToolAccessBulkSelectedTools', { count: selectedToolIds.size })}
        </div>
        <div className='toolAccess__bulkBar-employees'>
          <span className='toolAccess__bulkBar-label'>
            {t('ToolAccessBulkApplyTo')}
          </span>
          <div className='toolAccess__chips'>
            {employees.map((emp) => {
              const on = bulkEmployeeIds.has(emp.id);
              return (
                <button
                  key={emp.id}
                  type='button'
                  className='toolAccess__chip'
                  data-on={on ? 'true' : 'false'}
                  onClick={() => toggleBulkEmployee(emp.id)}
                >
                  {emp.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className='toolAccess__bulkBar-actions'>
          <MainSquareButton
            type='button'
            value={t('ToolAccessBulkEnable')}
            icon={tickSVG}
            buttonType='success'
            normal={true}
            loading={bulkBusy}
            disabled={
              bulkBusy ||
              selectedToolIds.size === 0 ||
              bulkEmployeeIds.size === 0
            }
            onClick={() => runBulk(true)}
          />
          <MainSquareButton
            type='button'
            value={t('ToolAccessBulkDisable')}
            icon={crossSmallSVG}
            buttonType='danger'
            normal={true}
            loading={bulkBusy}
            disabled={
              bulkBusy ||
              selectedToolIds.size === 0 ||
              bulkEmployeeIds.size === 0
            }
            onClick={() => runBulk(false)}
          />
        </div>
      </div>
    );
  };

  // ----------------------- render: a single tool row -----------------------
  const renderToolRow = (row: ToolAccessRow) => {
    const key = String(row.mcp_tool_policy_id);
    const busy = toggleBusy.has(key);
    const isWrite = row.classification === 'write';
    const isDefault = !row.is_overridden;
    const selectedForBulk = selectedToolIds.has(key);

    return (
      <div
        className='toolAccessRow'
        data-default={isDefault ? 'true' : 'false'}
        key={key}
      >
        <div className='toolAccessRow__main'>
          {bulkMode && canManage && (
            <input
              type='checkbox'
              className='toolAccessRow__check'
              checked={selectedForBulk}
              aria-label={t('ToolAccessSelectTool', {
                tool: row.exposed_name,
              })}
              onChange={() => toggleToolSelected(row.mcp_tool_policy_id)}
            />
          )}
          <div className='toolAccessRow__info'>
            <div className='toolAccessRow__nameLine'>
              <span className='toolAccessRow__name' title={row.tool_name}>
                {row.exposed_name}
              </span>
              {isWrite && (
                <span
                  className='toolAccessRow__writeBadge'
                  title={t('ToolAccessWriteBadgeHint')}
                >
                  <CustomSVGs svg={warningSVG} />
                  {t('ToolAccessWrite')}
                </span>
              )}
              {isDefault && (
                <span
                  className='toolAccessRow__defaultTag'
                  title={t('ToolAccessDefaultHint')}
                >
                  {t('ToolAccessDefault')}
                </span>
              )}
              {canManage && (
                <button
                  type='button'
                  className='toolAccessRow__aliasBtn'
                  title={t('ToolAccessEditAlias')}
                  aria-label={t('ToolAccessEditAlias')}
                  onClick={() => openAliasEdit(row)}
                >
                  <CustomSVGs svg={editSVG} />
                </button>
              )}
            </div>
            {row.ai_description && (
              <span
                className='toolAccessRow__desc'
                title={row.ai_description}
              >
                {row.ai_description}
              </span>
            )}
          </div>
        </div>

        {/* enablement toggle for the selected employee */}
        <div className='toolAccessRow__cell'>
          <button
            type='button'
            role='switch'
            aria-checked={row.employee_enabled}
            aria-label={t('ToolAccessToggleFor', {
              tool: row.exposed_name,
              employee: selectedEmployee?.name || '',
            })}
            className='toolAccessToggle'
            data-on={row.employee_enabled ? 'true' : 'false'}
            data-busy={busy ? 'true' : 'false'}
            disabled={!canManage || busy || !selectedEmployeeId}
            onClick={() => handleToggle(row)}
          >
            <span className='toolAccessToggle__knob' />
          </button>
        </div>
      </div>
    );
  };

  // ----------------------- render: a connection group -----------------------
  const renderGroup = (group: ConnectionGroup) => {
    const key = String(group.connectionId);
    const isCollapsed = collapsed.has(key);
    const groupWriteCount = group.rows.filter(
      (r) => r.classification === 'write'
    ).length;
    const allSelected =
      bulkMode &&
      group.rows.length > 0 &&
      group.rows.every((r) =>
        selectedToolIds.has(String(r.mcp_tool_policy_id))
      );

    return (
      <div className='toolAccessGroup' key={key}>
        <div className='toolAccessGroup__header'>
          <button
            type='button'
            className='toolAccessGroup__toggle'
            aria-expanded={!isCollapsed}
            onClick={() => toggleCollapse(group.connectionId)}
          >
            <span
              className='toolAccessGroup__caret'
              data-collapsed={isCollapsed ? 'true' : 'false'}
            />
            <span className='toolAccessGroup__name'>
              {group.connectionName}
            </span>
            <span className='toolAccessGroup__count'>
              {t('ToolAccessToolCount', { count: group.rows.length })}
              {groupWriteCount > 0 &&
                ` · ${t('ToolAccessWriteCount', {
                  count: groupWriteCount,
                })}`}
            </span>
          </button>
          {bulkMode && canManage && !isCollapsed && (
            <button
              type='button'
              className='toolAccessGroup__selectAll'
              onClick={() => selectAllToolsInGroup(group, !allSelected)}
            >
              {allSelected
                ? t('ToolAccessClearGroup')
                : t('ToolAccessSelectGroup')}
            </button>
          )}
        </div>

        {!isCollapsed && (
          <div className='toolAccessGroup__rows'>
            {group.rows.map(renderToolRow)}
          </div>
        )}
      </div>
    );
  };

  // ----------------------- render -----------------------
  if (!activeShopSlug) {
    return (
      <div className='controlCenter__banner'>
        {t('ControlCenterSelectBrand')}
      </div>
    );
  }
  if (!hasAgencyToken) {
    return (
      <div className='controlCenter__banner'>
        {t('ControlCenterNoAgencyToken')}
      </div>
    );
  }

  return (
    <div className='controlCenter__tabBody'>
      <div className='controlCenter__section'>
        <div className='controlCenter__sectionHeader'>
          <div className='controlCenter__sectionHeader-text'>
            <h2>{t('ToolAccessTitle')}</h2>
            <p>{t('ToolAccessSubtitle')}</p>
          </div>
        </div>

        {!canManage && (
          <div className='controlCenter__readonly'>
            {t('ToolAccessReadOnly')}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <LoadingSpinner />
        ) : rows.length === 0 ? (
          <TeachingEmptyState
            icon={chipSVG}
            title={t('ToolAccessEmptyTitle')}
            body={t('ToolAccessEmptyBody')}
          />
        ) : (
          <>
            {renderTopBar()}
            {renderBulkBar()}
            {/* legend */}
            <div className='toolAccess__legend'>
              <span className='toolAccess__legendItem'>
                <span className='toolAccessRow__writeBadge toolAccess__legendBadge'>
                  <CustomSVGs svg={warningSVG} />
                  {t('ToolAccessWrite')}
                </span>
                {t('ToolAccessLegendWrite')}
              </span>
              <span className='toolAccess__legendItem'>
                <span className='toolAccessRow__defaultTag toolAccess__legendBadge'>
                  {t('ToolAccessDefault')}
                </span>
                {t('ToolAccessLegendDefault')}
              </span>
              {writeCount > 0 && (
                <span className='toolAccess__legendItem toolAccess__legendItem--muted'>
                  {t('ToolAccessWriteCount', { count: writeCount })}
                </span>
              )}
            </div>

            <div className='toolAccessGrid'>{groups.map(renderGroup)}</div>
          </>
        )}
      </div>

      {/* ---------- alias edit popover (inline panel) ---------- */}
      {aliasEdit && (
        <div
          className='toolAccessAlias__overlay'
          role='dialog'
          aria-modal='true'
          aria-label={t('ToolAccessEditAlias')}
          onClick={closeAliasEdit}
        >
          <div
            className='toolAccessAlias'
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className='toolAccessAlias__title'>
              {t('ToolAccessEditAlias')}
            </h3>
            <p className='toolAccessAlias__sub'>
              {t('ToolAccessAliasSubtitle', { tool: aliasEdit.toolName })}
            </p>
            <label className='toolAccessAlias__label' htmlFor='ta-alias-input'>
              {t('ToolAccessAliasField')}
            </label>
            <input
              id='ta-alias-input'
              type='text'
              className='toolAccessAlias__input'
              value={aliasEdit.value}
              autoFocus
              disabled={aliasSaving}
              placeholder='my_renamed_tool'
              onChange={(e) =>
                setAliasEdit((prev) =>
                  prev ? { ...prev, value: e.target.value } : prev
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveAliasEdit();
                if (e.key === 'Escape') closeAliasEdit();
              }}
            />
            <span className='toolAccessAlias__hint'>
              {t('ToolAccessAliasRule')}
            </span>
            {aliasError && (
              <div className='toolAccessAlias__error'>{aliasError}</div>
            )}
            <div className='toolAccessAlias__actions'>
              <MainSquareButton
                value={t('Cancel')}
                buttonType='white'
                type='button'
                normal={true}
                disabled={aliasSaving}
                onClick={closeAliasEdit}
              />
              <MainSquareButton
                value={t('Save')}
                type='button'
                normal={true}
                loading={aliasSaving}
                disabled={aliasSaving}
                onClick={saveAliasEdit}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolAccessTab;
